import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { SubjectEdge } from '../../domain/entities/content.entity';
import { useSubjects } from '../hooks/use-subjects';
import {
  useSubjectEdges,
  useAddSubjectEdge,
  useRemoveSubjectEdge,
} from '../hooks/use-subject-edges';
import { useCanWriteSubjects } from '../hooks/use-content-permissions';
import { getApiErrorMessage, Mono } from '../display';
import { RefreshButton } from '@/shared/RefreshButton';

/** Máximo de assuntos carregados para os selects de aresta. */
const SUBJECTS_LIMIT = 200;

export function SubjectEdgesPanel() {
  const [parentId, setParentId] = useState('');
  const [childId, setChildId] = useState('');

  const { data: subjectsPage } = useSubjects({
    page: 1,
    perPage: SUBJECTS_LIMIT,
  });
  const { data: edges, isLoading, refetch, isFetching } = useSubjectEdges();
  const addEdge = useAddSubjectEdge();
  const removeEdge = useRemoveSubjectEdge();
  const canWrite = useCanWriteSubjects();

  const subjects = useMemo(() => subjectsPage?.items ?? [], [subjectsPage]);
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of subjects) map.set(s.id, s.name);
    return map;
  }, [subjects]);

  const options = [
    { value: '', label: 'Selecione…' },
    ...subjects.map((s) => ({ value: s.id, label: s.name })),
  ];

  const canAdd =
    parentId !== '' &&
    childId !== '' &&
    parentId !== childId &&
    !addEdge.isPending;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    addEdge.mutate(
      { parentId, childId },
      {
        onSuccess: () => {
          setParentId('');
          setChildId('');
        },
      },
    );
  };

  const columns: ColumnDef<SubjectEdge>[] = [
    {
      key: 'parentId',
      header: 'Pai',
      render: (row) => (
        <SubjectCell id={row.parentId} name={nameById.get(row.parentId)} />
      ),
    },
    {
      key: 'childId',
      header: 'Filho',
      render: (row) => (
        <SubjectCell id={row.childId} name={nameById.get(row.childId)} />
      ),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: '',
            render: (row: SubjectEdge) => (
              <div className="flex justify-end">
                <Button
                  variant="danger"
                  disabled={removeEdge.isPending}
                  onClick={() =>
                    removeEdge.mutate({
                      parentId: row.parentId,
                      childId: row.childId,
                    })
                  }
                >
                  Remover
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-(--color-text-secondary) max-w-xl text-sm">
          Arestas do grafo de assuntos (pai → filho). Usadas para navegar a
          hierarquia. Os selects listam os primeiros {SUBJECTS_LIMIT} assuntos.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <Link
            to="/content/graph"
            className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors"
          >
            Ver mapa visual →
          </Link>
        </div>
      </div>

      {canWrite && (
        <form
          onSubmit={handleAdd}
          className="border-(--color-border) bg-(--color-bg-secondary) grid grid-cols-1 items-end gap-3 rounded-lg border p-4 sm:grid-cols-[1fr_1fr_auto]"
        >
          <Select
            label="Assunto pai"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            options={options}
          />
          <Select
            label="Assunto filho"
            value={childId}
            onChange={(e) => setChildId(e.target.value)}
            options={options}
          />
          <Button type="submit" disabled={!canAdd}>
            {addEdge.isPending ? 'Adicionando…' : 'Adicionar aresta'}
          </Button>
        </form>
      )}

      {addEdge.error && (
        <p className="text-sm text-red-600">
          {getApiErrorMessage(addEdge.error, 'Falha ao adicionar aresta')}
        </p>
      )}
      {removeEdge.error && (
        <p className="text-sm text-red-600">
          {getApiErrorMessage(removeEdge.error, 'Falha ao remover aresta')}
        </p>
      )}

      <Table
        columns={columns}
        data={edges ?? []}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="Nenhuma aresta cadastrada."
        striped
      />
    </div>
  );
}

function SubjectCell({ id, name }: { id: string; name?: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-(--color-text-primary) text-sm">{name ?? '—'}</span>
      <Mono>{id}</Mono>
    </div>
  );
}
